import axios from "axios";
import api from "@/services/api";
import type {
	CreateExpensePayloadApi,
	ExpenseAttachmentDownloadUrlApiResponse,
	ExpenseById,
	ExpenseImportHeaderResponse,
	ExpenseImportPreviewResponse,
	ExpensesByVehicleFilters,
	ExpensesRankingResponseApi,
	UpdateExpensePayloadApi,
} from "../types";

const plainAxios = axios.create();

type ExpensePresignedPayload = {
	type: number;
	vehicleId: number;
	fileName: string;
	contentType: string;
};

type PresignedUrlResponse = {
	fileName: string;
	contentType: string;
	uploadUrl: string;
	fileKey: string;
};

export async function getExpensesRanking(
	filters: ExpensesByVehicleFilters = {}
): Promise<ExpensesRankingResponseApi> {
	const status = filters.status ?? 0;
	const pageNumber = filters.page ?? 1;
	const pageSize = filters.pageSize ?? 12;

	const { data } = await api.get<ExpensesRankingResponseApi>(
		"/api/expenses/ranking",
		{
			params: {
				Status: status,
				PageNumber: pageNumber,
				PageSize: pageSize,
			},
		}
	);
	return data;
}

export async function expensePresignedUrl(
	payload: ExpensePresignedPayload & { file: File }
): Promise<{ fileKey: string; fileName: string }> {
	const { file, type, vehicleId, fileName, contentType } = payload;

	const { data: presignedUrlResponse } = await api.post<PresignedUrlResponse>(
		"/api/expenses/attachment/presigned",
		{ type, vehicleId, fileName, contentType }
	);

	if (!presignedUrlResponse) {
		throw new Error("Presigned URL not found");
	}

	const { uploadUrl, fileKey } = presignedUrlResponse;

	await plainAxios.put(uploadUrl, file, {
		headers: { "Content-Type": contentType },
	});

	return { fileKey, fileName: presignedUrlResponse.fileName };
}

export async function createExpense(
	payload: CreateExpensePayloadApi
): Promise<{ id: number }> {
	const { data } = await api.post<{ id: number }>("/api/expenses", payload);
	return data;
}

export async function updateExpense(
	expenseId: string | number,
	payload: UpdateExpensePayloadApi
): Promise<void> {
	await api.put(`/api/expenses/${expenseId}`, payload);
}

export async function deleteExpense(id: string | number): Promise<void> {
	const { data } = await api.delete(`/api/expenses/${id}`);

	return data;
}

export async function getExpenseById(
	id: string | number
): Promise<ExpenseById> {
	const { data } = await api.get<ExpenseById>(`/api/expenses/${id}`);
	return data;
}

export async function getExpenseAttachmentDownloadUrl(
	expenseId: string | number
): Promise<ExpenseAttachmentDownloadUrlApiResponse> {
	const { data } = await api.get<ExpenseAttachmentDownloadUrlApiResponse>(
		`/api/expenses/${expenseId}/attachment/download-url`
	);
	return data;
}

export async function openExpenseAttachment(
	expenseId: string | number
): Promise<void> {
	const attachment = await getExpenseAttachmentDownloadUrl(expenseId);

	if (!(attachment.hasAttachment && attachment.url)) {
		throw new Error("Anexo da despesa não disponível.");
	}

	window.open(attachment.url, "_blank", "noopener,noreferrer");
}

export async function importExpensesHeader(
	type: number,
	file: File
): Promise<ExpenseImportHeaderResponse> {
	const formData = new FormData();
	formData.append("Type", String(type));
	formData.append("File", file);

	const { data } = await api.post<ExpenseImportHeaderResponse>(
		"/api/expenses/import/header",
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
		}
	);

	return data;
}

export async function importExpensesPreview(
	formData: FormData
): Promise<ExpenseImportPreviewResponse> {
	const { data } = await api.post<ExpenseImportPreviewResponse>(
		"/api/expenses/import/preview",
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
		}
	);

	return data;
}

export async function importExpensesConfirm(formData: FormData): Promise<void> {
	await api.post("/api/expenses/import/confirm", formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
}
